import { CommunityPostOverviewItemModel, HubDataSource } from "client/Api/Hub/hub";
import { ReactElement, useCallback } from "react";
import styles from "./Overview.module.less";
import { localize } from "client_legacy/ndaw/culture";
import { ViewAll } from "./ViewAll";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubCommunityView } from "../common";
import { ListLayout, ListLayoutCollapseMode } from "../ListLayout/ListLayout";
import { CommunityPostCardItem } from "./CommunityPostCardItem";

export type CommunityPostsSectionProps = {
	items: readonly CommunityPostOverviewItemModel[];
	isMobile: boolean;
	onViewChange: (view: HubCommunityView) => void;
	timezone: TimeZone;
};

export const CommunityPostsSection = (props: CommunityPostsSectionProps): ReactElement => {
	const {
		items,
		isMobile,
		onViewChange,
		timezone
	} = props;

	const viewMyFeedHandler = useCallback(
		(): void => {
			onViewChange({
				source: HubDataSource.Community,
				kind: "my-feed"
			});
		},
		[onViewChange]
	);

	const viewPostHandler = useCallback(
		(id: number): void => {
			onViewChange({
				source: HubDataSource.Community,
				kind: "post",
				id
			});
		},
		[onViewChange]
	);

	const viewCommunityHandler = useCallback(
		(communityId: number): void => {
			onViewChange({
				source: HubDataSource.Community,
				kind: "community",
				id: communityId
			});
		},
		[onViewChange]
	);

	return (
		<section>
			<header className={styles.sectionHeader}>
				<h3 className={styles.sectionTitle}>
					{localize("_LatestCommunityPosts")}
				</h3>

				{!isMobile && (
					<ViewAll
						itemSource={HubDataSource.Community}
						onClick={viewMyFeedHandler}
						variant="anchor"
					/>
				)}
			</header>

			<ListView
				isMobile={isMobile}
				items={items}
				trackingId="post-list-overview"
				onNavigateToItem={viewPostHandler}
				onNavigateToCommunity={viewCommunityHandler}
				collapseMode={{
					kind: isMobile
						? "limited-items"
						: "one-row",
					isCollapsed: true,
					maxItemsCount: isMobile
						? MAX_MOBILE_ITEMS_TO_RENDER
						: Number.MAX_SAFE_INTEGER
				}}
				shouldAlwaysRenderMoreButton
				renderMoreButton={
					isMobile
						? (
							<ViewAll
								itemSource={HubDataSource.Community}
								onClick={viewMyFeedHandler}
								variant="list-item"
							/>
						)
						: null
				}
				timezone={timezone}
			/>
		</section>
	);
};
CommunityPostsSection.displayName = "CommunityPostsSection";

const MAX_MOBILE_ITEMS_TO_RENDER = 3;

type ListViewProps = {
	isMobile: boolean;
	items: readonly CommunityPostOverviewItemModel[];
	onNavigateToItem: (id: number) => void;
	onNavigateToCommunity: (communityId: number) => void;
	trackingId: string;
	shouldReserveSpaceForFloatingActionButton?: boolean;
	timezone: TimeZone;
} & (
	{
		collapseMode?: undefined;
	}
	|
	{
		collapseMode: ListLayoutCollapseMode;
		renderMoreButton: ReactElement | null;
		shouldAlwaysRenderMoreButton?: boolean;
	}
);

const ListView = (props: ListViewProps): ReactElement => {
	const {
		isMobile,
		items,
		trackingId,
		shouldReserveSpaceForFloatingActionButton,
		onNavigateToItem,
		onNavigateToCommunity,
		timezone
	} = props;

	return (
		<ListLayout
			{...(
				props.collapseMode !== undefined
					? {
						collapseMode: props.collapseMode,
						renderMoreButton: props.renderMoreButton,
						shouldAlwaysRenderMoreButton: props.shouldAlwaysRenderMoreButton
					}
					: {
						collapseMode: undefined
					}
			)}
			trackingId={trackingId}
			hasBorderTop={isMobile}
			renderAfterItems={(
				<>
					{shouldReserveSpaceForFloatingActionButton && (
						<ListLayout.MobilePlusMenuSpacing />
					)}
				</>
			)}
		>
			{items.map(item => {
				const {
					id,
					community: {
						id: communityId
					}
				} = item;

				return (
					<ListLayout.Item
						key={id}
					>
						<CommunityPostCardItem
							model={item}
							onClick={() => onNavigateToItem(id)}
							onNavigateToCommunity={() => onNavigateToCommunity(communityId)}
							trackingId={`community-post-item|${id}`}
							timezone={timezone}
						/>
					</ListLayout.Item>
				);
			})}
		</ListLayout>
	);
};
ListView.displayName = "ListView";
